import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import Text from '@/shared/ui/components/basic/Text';
import { SearchAutocomplete, type SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { getAgencyClients } from '@/services/requests/housien-services/ClientsService';
import type { Client } from '@/services/requests/housien-models/Client';

/**
 * Derives a display name from a Client record.
 */
function clientDisplayName(c: Client, t: TFunction): string {
  if (c.personal_info?.full_name) return c.personal_info.full_name;
  if (c.identity?.trade_name) return c.identity.trade_name;
  if (c.identity?.corporate_name) return c.identity.corporate_name;
  const id = c.metadata?.client_id ?? '';
  return t('drawer.clientFallbackName', { id: id.length > 8 ? `${id.slice(0, 8)}…` : id });
}

/**
 * Client picker and reserve button for an available unit on an open launch.
 */
export function LaunchUnitHoldForm({
  agencyId,
  unitId,
  loading,
  onHold,
}: {
  agencyId: string;
  unitId: string;
  loading: boolean;
  onHold: (unitId: string, clientId: string) => void;
}) {
  const { t } = useTranslation('features/launches');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);

  useEffect(() => {
    if (!agencyId) return;
    getAgencyClients(agencyId, 1, 20).then((res) => {
      setClients(res.data ?? []);
    }).catch(() => setClients([]));
  }, [agencyId]);

  const clientOptions: SearchAutocompleteOption[] = useMemo(
    () => clients.map((c) => ({
      id: c.metadata?.client_id ?? '',
      label: clientDisplayName(c, t),
      sublabel: c.assignment?.contacts?.[0]?.value ?? c.identity?.cpf ?? '',
    })).filter((o) => o.id),
    [clients, t],
  );

  return (
    <>
      <div className="flex flex-col gap-[2px]">
        <Text as="label" className="text-[length:var(--text-meta)] font-medium text-(--text-tertiary)">
          {t('drawer.client')}
        </Text>
        <SearchAutocomplete
          placeholder={t('drawer.clientPlaceholder')}
          options={clientOptions}
          value={selectedClientId}
          onChange={(val) => setSelectedClientId(val ?? null)}
        />
        {clientOptions.length > 0 && !selectedClientId && (
          <div className="mt-[var(--space-1)]">
            <Text as="span" className="text-[length:var(--text-micro)] text-(--text-tertiary)">
              {t('drawer.recentClients')}
            </Text>
            <div className="flex flex-wrap gap-[var(--space-1)] mt-[2px]">
              {clientOptions.slice(0, 5).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedClientId(c.id)}
                  className={cn(
                    'rounded-(--radius-md) border border-(--border-subtle)',
                    'px-[var(--space-2)] py-[var(--space-0-5)]',
                    'text-[length:var(--text-micro)] text-(--text-secondary)',
                    'hover:bg-(--surface-secondary) hover:text-(--text-primary)',
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        disabled={loading || !selectedClientId}
        onClick={() => onHold(unitId, selectedClientId ?? '')}
        className={cn(
          'rounded-(--radius-md) px-[var(--space-3)] py-[var(--space-2)] w-full',
          'text-[length:var(--text-small)] font-[var(--font-medium)]',
          'bg-[var(--seed-base)] text-[var(--text-inverse)] hover:opacity-90',
          (loading || !selectedClientId) && 'opacity-50 cursor-not-allowed',
        )}
      >
        {t('actions.hold')}
      </button>
    </>
  );
}
